/**
 * cortex-admin の oRPC contract（contract-first）。
 *
 * API（apps/api/admin）はこの contract を `implement` してハンドラを実装し、Web
 * （apps/web/cortex-admin）は `ContractRouterClient<typeof cortexAdminContract>` として型付き
 * クライアントを生成する。入出力スキーマは本 package の zod schema を SoT として共有するため、
 * API と Web の通信は手書きの型キャストや URL 文字列ではなく、この contract の型から自動導出される。
 *
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business cortex-admin の API ↔ Web 通信を oRPC contract-first で型共有し、エンドポイント単位の型ドリフトを排除する。
 * @graph-connects none
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { CurrentUserResponseSchema, LogoutResponseSchema } from './auth.js';
import { PipelineConfigIdInputSchema, TriggerPipelineInputSchema, PipelineConfigListResponseSchema, PipelineConfigSchema, TriggerPipelineResponseSchema, } from './pipeline.js';
import { PipelinePermissionsSchema, SavePipelinePermissionsRequestSchema } from './permissions.js';
import { RevokeWorkspacePermissionResponseSchema, SaveWorkspacePermissionAdminsRequestSchema, UpsertWorkspacePermissionInputSchema, WorkspacePermissionAdminsSchema, WorkspacePermissionEmailInputSchema, WorkspacePermissionListSchema, WorkspacePermissionSchema, } from './workspace-permissions.js';
import { WorkspaceUserListSchema } from './user.js';
import { CreateDataViewerJobRequestSchema, DatabaseListResponseSchema, DataViewerJobDetailSchema, DataViewerJobIdInputSchema, DataViewerJobIdResponseSchema, DataViewerJobListResponseSchema, DataViewerTableListResponseSchema, DescribeDataViewerTableResponseSchema, PreviewJobQueryRequestSchema, PreviewQueryTablesRequestSchema, PreviewTableColumnsRequestSchema, QueryResultSchema, RunDataViewerJobInputSchema, UpdateDataViewerJobRequestSchema, } from './data-viewer.js';
import { InfraGraphOverviewResponseSchema, InfraGraphStackDetailResponseSchema, InfraGraphStackInputSchema, } from './infra-graph.js';
import { MarkOwnershipTransferDeletedRequestSchema, MarkOwnershipTransferKeptRequestSchema, MarkOwnershipTransferReassignedRequestSchema, OwnershipTransferIdInputSchema, OwnershipTransferListFilterSchema, OwnershipTransferListResponseSchema, OwnershipTransferRecordSchema, } from './ownership-transfers.js';
import { BotStatusLatestInputSchema, BotStatusLatestResponseSchema, BotStatusReportRequestSchema, BotStatusReportResponseSchema, } from './bot-status.js';
import { ClaudeUsageLatestInputSchema, ClaudeUsageLatestResponseSchema } from './claude-usage.js';
import { GenerateCaptionRequestSchema, GenerateCaptionResponseSchema, GenerateImageRequestSchema, GenerateImageResponseSchema, GeneratePagePromptsRequestSchema, GeneratePagePromptsResponseSchema, GenerateThemesRequestSchema, GenerateThemesResponseSchema, GenerateTitleRequestSchema, GenerateTitleResponseSchema, RecordContentDownloadRequestSchema, RecordContentDownloadResponseSchema, TiktokAuthUrlRequestSchema, TiktokAuthUrlResponseSchema, TiktokCallbackRequestSchema, TiktokCallbackResponseSchema, TiktokCreateDraftRequestSchema, TiktokCreateDraftResponseSchema, TiktokGetStatusRequestSchema, TiktokGetStatusResponseSchema, } from './sns-content-generator.js';
/**
 * admin API が返す代表的なエラー集合を持つ contract builder。
 * @graph-connects none
 */
const base = oc.errors({
    UNAUTHORIZED: { message: 'ログインが必要です' },
    FORBIDDEN: { message: 'アクセス権限がありません' },
    NOT_FOUND: { message: 'リソースが見つかりません' },
    BAD_REQUEST: { message: 'リクエストが不正です' },
    CONFLICT: { message: '既に存在します' },
    INTERNAL_SERVER_ERROR: { message: '内部エラーが発生しました' },
});
/**
 * auth feature の oRPC contract（me / logout）。
 * @graph-connects none
 */
export const authContract = {
    me: base.output(CurrentUserResponseSchema),
    logout: base.output(LogoutResponseSchema),
};
/**
 * pipeline config feature の oRPC contract（一覧 / 詳細 / 手動実行）。
 * @graph-connects none
 */
export const pipelinesContract = {
    list: base.output(PipelineConfigListResponseSchema),
    get: base.input(PipelineConfigIdInputSchema).output(PipelineConfigSchema),
    trigger: base.input(TriggerPipelineInputSchema).output(TriggerPipelineResponseSchema),
};
/**
 * pipeline permissions feature の oRPC contract（取得 / 保存）。
 * @graph-connects none
 */
export const pipelinePermissionsContract = {
    get: base.output(PipelinePermissionsSchema),
    save: base.input(SavePipelinePermissionsRequestSchema).output(PipelinePermissionsSchema),
};
/**
 * Workspace 権限（cortex_stack_permissions）feature の oRPC contract。
 *
 * `saveAdmins`（配れる人を増やす操作）は画面からだけ使う。MCP 側には対応するツールを置かない。
 * @graph-connects none
 */
export const workspacePermissionsContract = {
    get: base.output(WorkspacePermissionListSchema),
    upsert: base.input(UpsertWorkspacePermissionInputSchema).output(WorkspacePermissionSchema),
    revoke: base
        .input(WorkspacePermissionEmailInputSchema)
        .output(RevokeWorkspacePermissionResponseSchema),
    saveAdmins: base
        .input(SaveWorkspacePermissionAdminsRequestSchema)
        .output(WorkspacePermissionAdminsSchema),
};
/**
 * workspace users feature の oRPC contract（一覧）。
 * @graph-connects none
 */
export const usersContract = {
    list: base.output(WorkspaceUserListSchema),
};
/**
 * data-viewer feature の oRPC contract。
 * @graph-connects none
 */
export const dataViewerContract = {
    listJobs: base.output(DataViewerJobListResponseSchema),
    runJob: base.input(RunDataViewerJobInputSchema).output(QueryResultSchema),
    exportJob: base.input(RunDataViewerJobInputSchema).output(QueryResultSchema),
    createJob: base.input(CreateDataViewerJobRequestSchema).output(DataViewerJobIdResponseSchema),
    previewJobQuery: base.input(PreviewJobQueryRequestSchema).output(QueryResultSchema),
    getJobDetail: base.input(DataViewerJobIdInputSchema).output(DataViewerJobDetailSchema),
    updateJob: base
        .input(z.object({
        id: DataViewerJobIdInputSchema.shape.id,
        input: UpdateDataViewerJobRequestSchema,
    }))
        .output(DataViewerJobIdResponseSchema),
    deleteJob: base.input(DataViewerJobIdInputSchema).output(DataViewerJobIdResponseSchema),
    listDatabases: base.output(DatabaseListResponseSchema),
    previewQueryTables: base
        .input(PreviewQueryTablesRequestSchema)
        .output(DataViewerTableListResponseSchema),
    previewTableColumns: base
        .input(PreviewTableColumnsRequestSchema)
        .output(DescribeDataViewerTableResponseSchema),
};
/**
 * infra-graph feature の oRPC contract（俯瞰 / stack 詳細）。
 * @graph-connects none
 */
export const infraGraphContract = {
    overview: base.output(InfraGraphOverviewResponseSchema),
    stack: base.input(InfraGraphStackInputSchema).output(InfraGraphStackDetailResponseSchema),
};
/**
 * ownership_transfers feature の oRPC contract。
 * 退職時に自動で admin (r.tsuji@) に移譲された cortex リソースの監査レコードを admin が
 * 一覧 / 詳細表示し、最終的に `kept` (admin 所有のまま) / `reassigned` (別の owner へ再移譲) /
 * `deleted` (元 doc を実削除) で決着するための endpoint を提供する。
 * @graph-connects none
 */
export const ownershipTransfersContract = {
    list: base.input(OwnershipTransferListFilterSchema).output(OwnershipTransferListResponseSchema),
    get: base.input(OwnershipTransferIdInputSchema).output(OwnershipTransferRecordSchema),
    markKept: base
        .input(MarkOwnershipTransferKeptRequestSchema)
        .output(OwnershipTransferRecordSchema),
    markReassigned: base
        .input(MarkOwnershipTransferReassignedRequestSchema)
        .output(OwnershipTransferRecordSchema),
    markDeleted: base
        .input(MarkOwnershipTransferDeletedRequestSchema)
        .output(OwnershipTransferRecordSchema),
};
/**
 * sns-content-generator feature の oRPC contract（テーマ生成 / タイトル生成 / 画像プロンプト生成 /
 * 画像生成 / ダウンロード記録 / キャプション生成 / TikTok連携）。各 procedure は「LLM 1回、
 * または画像1枚」の粒度に切ってある（5章参照。admin-api Cloud Run timeout 180s のため）。
 * TikTok下書き送信のステータス確認は `tiktokCreateDraft` に含めず `tiktokGetStatus` へ分離し、
 * ポーリングを admin-api に跨がせない（12回×5秒=最大60秒を同期で待つと timeout を圧迫する）。
 * トークンの1日1回リフレッシュ（`/tiktok/refresh`）は Cloud Scheduler が Python サービスを
 * 直接叩くため、この contract には含まない。
 * @graph-connects none
 */
export const snsContentGeneratorContract = {
    generateThemes: base.input(GenerateThemesRequestSchema).output(GenerateThemesResponseSchema),
    generateTitle: base.input(GenerateTitleRequestSchema).output(GenerateTitleResponseSchema),
    generatePagePrompts: base
        .input(GeneratePagePromptsRequestSchema)
        .output(GeneratePagePromptsResponseSchema),
    generateImage: base.input(GenerateImageRequestSchema).output(GenerateImageResponseSchema),
    recordContentDownload: base
        .input(RecordContentDownloadRequestSchema)
        .output(RecordContentDownloadResponseSchema),
    generateCaption: base.input(GenerateCaptionRequestSchema).output(GenerateCaptionResponseSchema),
    tiktokAuthUrl: base.input(TiktokAuthUrlRequestSchema).output(TiktokAuthUrlResponseSchema),
    tiktokCallback: base.input(TiktokCallbackRequestSchema).output(TiktokCallbackResponseSchema),
    tiktokCreateDraft: base
        .input(TiktokCreateDraftRequestSchema)
        .output(TiktokCreateDraftResponseSchema),
    tiktokGetStatus: base.input(TiktokGetStatusRequestSchema).output(TiktokGetStatusResponseSchema),
};
/**
 * bot-status feature の oRPC contract（母艦 collector からのレポート受理 / 最新レポート取得）。
 *
 * `report` は **人ではなく機械が叩く**。母艦の collector が `x-api-key: $CORTEX_API_KEY` を付けて
 * POST し、edge-router Worker が mcp-auth に検証を委譲したうえで `X-Cortex-User-*` を注入する。
 * cc-analytics-ingest が同じ経路で CLI からの usage を受けている。
 *
 * @graph-connects none
 */
export const botStatusContract = {
    report: base.input(BotStatusReportRequestSchema).output(BotStatusReportResponseSchema),
    latest: base.input(BotStatusLatestInputSchema).output(BotStatusLatestResponseSchema),
};
/**
 * claude-usage feature の oRPC contract（bot 別の Claude 利用状況）。
 *
 * read-only。母艦の bot が Grafana Cloud へ送った OTel テレメトリを読むだけで、
 * bot-status のような write（`report`）は無い。
 *
 * @graph-connects none
 */
export const claudeUsageContract = {
    latest: base.input(ClaudeUsageLatestInputSchema).output(ClaudeUsageLatestResponseSchema),
};
/**
 * Cortex Admin API 全体の oRPC contract。feature 単位でネストし、Web は
 * `client.dataViewer.listJobs()` のように feature.procedure で型付き呼び出しする。
 * @graph-connects none
 */
export const cortexAdminContract = {
    auth: authContract,
    pipelines: pipelinesContract,
    pipelinePermissions: pipelinePermissionsContract,
    workspacePermissions: workspacePermissionsContract,
    users: usersContract,
    dataViewer: dataViewerContract,
    infraGraph: infraGraphContract,
    ownershipTransfers: ownershipTransfersContract,
    snsContentGenerator: snsContentGeneratorContract,
    botStatus: botStatusContract,
    claudeUsage: claudeUsageContract,
};
//# sourceMappingURL=orpc-contract.js.map